'use client';

import React from 'react';

import { useRouter } from 'next/navigation';

import { ArrowLeft } from 'lucide-react';

/**
 * Persistent native auth layout shell.
 * Rendered once in the auth layout — stays mounted across sign-in / sign-up
 * navigation so the header never flashes.
 *
 * Visual tokens come from the same source as the native home screen
 * (appBackgroundColor / appTextColor) so login and home stay in sync.
 * Brand primary/secondary colors are intentionally NOT used here — those are
 * reserved for the website experience.
 */
export function NativeAuthLayoutShell({
  children,
  logoUrl,
  accountName,
  appBackgroundColor,
  appTextColor,
  onBack,
  hasBottomTabs = false,
}: {
  children: React.ReactNode;
  logoUrl?: string | null;
  accountName?: string;
  /** Native home background — drives the shell + header background. */
  appBackgroundColor?: string | null;
  /** Native home text color — drives all foreground ink. */
  appTextColor?: string | null;
  onBack?: () => void;
  hasBottomTabs?: boolean;
}) {
  const router = useRouter();

  // Match useNativeTokens() defaults so login and home render identically.
  const surfaceBg = appBackgroundColor || '#FFFFFF';
  const inkColor = appTextColor || '#1C1C1C';

  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{
        backgroundColor: surfaceBg,
        color: inkColor,
        // Clear the iPhone Duo system bar column and landscape cutouts.
        paddingLeft: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
        paddingRight: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
        paddingBottom: hasBottomTabs ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : undefined,
      }}
    >
      {/* Fixed header — matches NativeHeader from native-layout-shell */}
      <header
        className="fixed top-0 z-40 px-2 pb-1"
        style={{
          left: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
          right: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
          backgroundColor: surfaceBg,
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4px)',
          borderBottom: `1px solid ${inkColor}14`,
        }}
      >
        <div className="relative flex h-11 items-center">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onBack ?? (() => router.back())}
              className="flex h-11 w-11 items-center justify-center rounded-full transition-colors active:bg-white/10"
              aria-label="Go back"
            >
              <ArrowLeft
                className="h-5 w-5 rtl:rotate-180"
                style={{ color: inkColor }}
              />
            </button>

            {logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                dir="ltr"
                src={logoUrl}
                alt={accountName ?? ''}
                className="h-7 w-auto object-contain"
                draggable={false}
              />
            ) : accountName ? (
              <span
                dir="ltr"
                className="text-xl font-bold capitalize"
                style={{ color: inkColor }}
              >
                {accountName}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      {/* Spacer matching the fixed header height */}
      <div style={{ height: 'calc(env(safe-area-inset-top, 0px) + 48px)' }} />

      {/* Page content */}
      <main className="flex flex-1 flex-col">{children}</main>
    </div>
  );
}
